import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

/**
 * Renders a Strapi media descriptor (see mediaSource in src/lib/predio.js) as a
 * responsive <img>.
 *
 * Format selection is handled server-side by Accept negotiation, so there is no
 * <picture>/<source> here -- the same URL yields AVIF, WebP or the original
 * depending on the browser. This component's job is resolution: srcset/sizes,
 * intrinsic dimensions to avoid layout shift, and the right loading priority.
 */
export default function SmartImage({ img, alt, sizes, className, priority, lightbox }) {
  const ref = useRef(null);

  useEffect(() => {
    // React 16 drops unknown attributes, and fetchpriority is not in its known
    // set, so it has to be applied straight to the node.
    if (priority && ref.current) {
      ref.current.setAttribute("fetchpriority", "high");
    }
  }, [priority]);

  if (!img) return null;

  return (
    <img
      ref={ref}
      className={className}
      src={img.src}
      srcSet={img.srcSet}
      sizes={img.srcSet ? sizes : undefined}
      width={img.width}
      height={img.height}
      alt={alt || ""}
      loading={priority ? "eager" : "lazy"}
      decoding={priority ? "auto" : "async"}
      // The lightbox reads this so grid thumbnails stay small while the overlay
      // still opens the full-size file.
      data-full={lightbox ? img.full : undefined}
    />
  );
}

SmartImage.propTypes = {
  img: PropTypes.shape({
    src: PropTypes.string,
    srcSet: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    full: PropTypes.string,
  }),
  alt: PropTypes.string,
  sizes: PropTypes.string,
  className: PropTypes.string,
  priority: PropTypes.bool,
  lightbox: PropTypes.bool,
};

SmartImage.defaultProps = {
  sizes: "100vw",
  priority: false,
  lightbox: false,
};
